import React, { useEffect, useState, Fragment } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import { useTranslation } from 'react-i18next';
import TableToolbar from '../../components/Table/toolbar';
import LoadMore from '../../components/Table/load-more';
import EnhancedTableHead from '../../components/Table/enhanced-table-head';
import { useDispatch } from 'react-redux';
import { alert } from '../../redux/ducks/notification';
import Moment from 'react-moment';
import moment from 'moment';
import SearchBar from '../../components/Filters/search-bar';
import { statuses } from '../../constants/statuses';
import { locations } from '../../constants/locations';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import { Tooltip } from '@material-ui/core';
import { getIncidencesByType } from '../../services/api/incidence-api-service';
import { incidence } from '../../constants/incidence';

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
  },
  paper: {
    marginBottom: theme.spacing(3),
    overflow: 'hidden',
  },
  tableContainer: {
    overflowX: 'auto',
  },
  table: {
    minWidth: 650,
  },
  link: {
    cursor: 'pointer'
  },
  addButton: {
    marginBottom: theme.spacing(2),
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
  }
}));

const Incidences = ({ history }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [incidences, setIncidences] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [next, setNext] = useState(undefined);
  const [selector, setSelector] = useState('');
  const [value, setValue] = useState('');
  const [from, setFrom] = useState(moment());
  const [to, setTo] = useState(moment());
  const [status, setStatus] = useState('');
  const [location, setLocation] = useState('');

  const [order, setOrder] = useState({
    column: 'number',
    direction: 'asc'
  });

  const headCells = [
    { id: 'number', label: t('tables.incidence.thead.number'), sortable: true },
    { id: 'createdAt', label: t('tables.incidence.thead.created_at'), sortable: true },
    { id: 'customer', label: t('tables.incidence.thead.customer'), sortable: true },
    { id: 'items', label: t('tables.incidence.thead.items'), sortable: false },
    { id: 'status', label: t('tables.incidence.thead.status'), sortable: false },
  ];

  const fields = [
    { value: 'number', name: t('tables.incidence.thead.number') },
    { value: 'customer', name: t('tables.incidence.thead.customer') },
    { value: 'items', name: t('tables.incidence.thead.items') },
    { value: 'createdAt', name: t('tables.incidence.thead.created_at') },
  ];

  const handleRequestSort = (event, property) => {
    const isAsc = order.column === property && order.direction === 'asc';
    setOrder(prevState => {
      return {
        ...prevState,
        column: property,
        direction: isAsc ? 'desc' : 'asc'
      }
    })
  };

  const fetchIncidences = async (filters = null, loadMore = false) => {
    if (null === filters) {
      filters = buildFilters();
    }

    try {
      const response = await getIncidencesByType(incidence.internal, filters);
      const data = response.data;
      setIncidences(loadMore ? incidences.concat(data['items']) : data['items']);
      setTotalItems(data['meta'].total);
      setNext(true === data['meta'].hasNext ? `${filters.replace(/&page=\d+$/, '')}&page=${data['meta'].page + 1}` : undefined);
    } catch (e) {
      dispatch(alert(t('errors.common.server')));
    }
  };

  useEffect(() => {
    fetchIncidences();
  }, [order]); // eslint-disable-line

  const handleClick = id => {
    history.push(`/dashboard/incidences/${id}`);
  };

  const handleSelector = event => {
    setSelector(event.target.value);
  };

  const handleValue = event => {
    setValue(event.target.value);
  };

  const handleFrom = data => {
    setFrom(moment(data))
  };

  const handleTo = data => {
    setTo(moment(data))
  };

  const handleStatus = event => {
    setStatus(event.target.value);
  };

  const handleLocation = event => {
    setLocation(event.target.value);
  };

  const buildFilters = () => {
    let filters = `&sort=${order.column}&order=${order.direction}`;

    switch (selector) {
      case 'number':
        filters += `&number=${value}`;
        break;
      case 'createdAt':
        filters += `&createdAfter=${moment(from).format('Y-MM-DD')}&createdBefore=${moment(to).format('Y-MM-DD')}`;
        break;
      case 'customer':
        filters += `&customer=${value}`;
        break;
      case 'items':
        filters += `&items=${value}`;
        break;
      default:
        filters += ''
    }

    if (status.length > 0) {
      filters += `&status=${status}`
    }

    if (location.length > 0) {
      filters += `&location=${location}`
    }

    return filters;
  };

  const handleSubmit = event => {
    event.preventDefault();
    let filters = buildFilters();
    fetchIncidences(filters);
  };

  const content = (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <TableToolbar name={`${t('tables.incidence.name')} (${totalItems})`} />
        <TableContainer className={classes.tableContainer}>
          <Table className={classes.table} size="small">
            <EnhancedTableHead
              order={order.direction}
              orderBy={order.column}
              onRequestSort={handleRequestSort}
              headCells={headCells}
            />
            <TableBody>
              {incidences.map((row) => (
                <TableRow className={classes.link} hover key={row.id} onClick={() => handleClick(row.id)}>
                  <TableCell>{row.number}</TableCell>
                  <TableCell>
                    <Moment format={'DD-MM-YYYY'}>
                      {row.createdAt}
                    </Moment>
                  </TableCell>
                  <TableCell>{`${row.customer.name || ''} ${row.customer.lastName || ''}`}</TableCell>
                  <TableCell>{row.items}</TableCell>
                  <TableCell>{t(`status.${row.status.name}`)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {next &&
        <LoadMore buttonText={t('tables.common.load_more')} loadMore={() => fetchIncidences(next, true)} />
        }
      </Paper>
    </div>
  );

  return (
    <Fragment>
      <Box className={classes.addButton}>
        <Tooltip title={t('tooltip.incidence.create')} placement={'right'} arrow>
          <AddCircleIcon
            onClick={() => history.push('/dashboard/incidences/create')}
            fontSize={'large'}
            color={'primary'}
          />
        </Tooltip>
      </Box>
      <SearchBar
        from={from}
        handleFrom={handleFrom}
        to={to}
        handleTo={handleTo}
        handleSubmit={handleSubmit}
        selector={selector}
        handleSelector={handleSelector}
        fields={fields}
        value={value}
        handleValue={handleValue}
        status={status}
        handleStatus={handleStatus}
        statuses={statuses}
        location={location}
        handleLocation={handleLocation}
        locations={locations}
      />
      {totalItems > 0 ? content : (
        <Paper className={classes.emptyState}>
          <Typography variant="body1" color="textSecondary">
            {t('tables.common.no_data')}
          </Typography>
        </Paper>
      )}
    </Fragment>
  );
};

export default Incidences;
